import React from 'react';
import PropTypes from 'prop-types';
import { graphql, navigate, useStaticQuery } from 'gatsby';
import { useLocation } from '@reach/router';
import styled from '@emotion/styled';

import useUserContext from '../hooks/use-user-context';
import { createOptionMap } from '../utilities/misc';
import { rem } from '../utilities/style';
import { generateLanguagePath } from '../utilities/url';
import { colors } from '../theme/colors';
import { media } from '../theme/media';
import { containerStyles } from '../styles/layout';

import Link from './Link';
import TextRenderer from './TextRenderer';

const Container = styled.nav`
  background: ${colors.white};
  overflow-x: auto;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, .10);
  margin: 1px 0;
`;

const NavWrapper = styled.ol`
  ${containerStyles.regular}
  display: flex;
`;

const NavItem = styled.li`
  display: flex;
  align-items: center;

  &:first-of-type {
    border-left: 1px solid rgba(0, 0, 0, .10);
  }
`;

const NavLink = styled(Link)`
  display: block;
  padding: 0.75em;
  border-right: 1px solid rgba(0, 0, 0, .10);
  font-size: ${rem(14)};
  color: ${colors.gray['600']};
  white-space: nowrap;

  ${media('>tiny')} {
    font-size: ${rem(16)};
  }

  &[href]:focus,
  &[href]:hover {
    background: ${colors.gray['50']};
    color: ${colors.blue['800']};
  }
`;

const Breadcrumbs = ({ items }) => {
  const data = useStaticQuery(graphql`
    query Breadcrumbs {
      allContentfulOptionSet(filter: {name: {eq: "breadcrumbs"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { state } = useLocation();
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);

  const handleClick = (event, item) => {
    if (state && state.prevUrl && item.backButton) {
      const { pathname, search } = new URL(state.prevUrl);
      const urlWithLanguage = generateLanguagePath(language, item.url);

      // Only use previous url if it matches the target
      if (pathname.startsWith(urlWithLanguage)) {
        event.preventDefault();

        navigate(`${pathname}${search}`);
      }
    }
  };

  return (
    <Container aria-label={options['breadcrumbs.nav_label']}>
      <NavWrapper itemScope itemType="https://schema.org/BreadcrumbList">
        {items.map((item, index) => (
          <NavItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <NavLink
              as={item.url ? null : 'span'}
              to={item.url}
              activeStyle={null}
              aria-current={item.current ? 'page' : null}
              onClick={(event) => handleClick(event, item)}
              itemProp={item.url ? 'item' : null}
            >
              <span itemProp="name">
                <TextRenderer text={item.title} />
              </span>
              <meta itemProp="position" content={index + 1} />
            </NavLink>
          </NavItem>
        ))}
      </NavWrapper>
    </Container>
  );
};

Breadcrumbs.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
      current: PropTypes.bool,
      backButton: PropTypes.bool
    })
  )
};

export default Breadcrumbs;
